/**
 * Anything that can say which workspace it belongs to.
 */
export interface WorkspaceScoped {
  workspaceId?: string | null;
}

/**
 * Narrows a run of seasons to the workspace chosen in the header.
 *
 * The picker is per page, but the choice is global, so every page that lists seasons has to apply
 * it or the header quietly means nothing. The choice holds until it is changed again: there is no
 * exception for the season being read, because a picker that keeps one campaign from elsewhere
 * reads as an unfiltered list and makes that campaign look like it belongs to the chosen
 * workspace, which was the confusion this rule exists to remove.
 *
 * A season that cannot say which workspace it belongs to is kept, because dropping it on missing
 * data would lose it rather than file it.
 *
 * "All workspaces" (a null selection) keeps everything.
 *
 * This lives in `lib` rather than beside the hook so the registrations provider — which sits below
 * the workspace provider and picks the nav's active challenge — can apply the same rule without
 * importing the hook that reads from it.
 */
export function filterSeasonsByWorkspace<T extends WorkspaceScoped>(
  seasons: readonly T[],
  selectedWorkspaceId: string | null,
): T[] {
  if (!selectedWorkspaceId) return [...seasons];
  return seasons.filter(
    season => !season.workspaceId || season.workspaceId === selectedWorkspaceId,
  );
}
